import styled, { createGlobalStyle } from 'styled-components';

// ── Global print styles (A4, exact colours) ───────────────────────────────────

export const PdfPrintGlobalStyle = createGlobalStyle`
  @media print {
    @page {
      size: A4;
      margin: 14mm 16mm 14mm 16mm;
    }

    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    html, body {
      background: #fff !important;
    }
  }
`;

// ── A4 page container ─────────────────────────────────────────────────────────

export const PdfPageWrapper = styled.div`
  font-family: 'Mulish', 'Helvetica Neue', Arial, sans-serif;
  font-size: 10.5px;
  color: #1a1a1a;
  background: #fff;
  line-height: 1.45;

  @media screen {
    max-width: 210mm;
    margin: 0 auto;
    padding: 16mm;
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.1);
    min-height: 297mm;
    box-sizing: border-box;
  }

  @media print {
    box-shadow: none;
    padding: 0;
  }
`;

// ── Print controls bar (sticky; hidden in print) ──────────────────────────────

export const PdfControlsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  position: sticky;
  top: 0;
  z-index: 100;

  @media print {
    display: none;
  }
`;

// ── Section card wrapper ──────────────────────────────────────────────────────

export const PdfSectionCard = styled.div`
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  padding: 10px 12px 12px;
  margin-bottom: 8px;
  page-break-inside: avoid;
  break-inside: avoid;

  @media print {
    page-break-inside: auto;
    break-inside: auto;
  }
`;

export const PdfSectionLabel = styled.div`
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

// ── Badges ────────────────────────────────────────────────────────────────────

export const PdfBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

/*
 * shape='pill' (default) → highly rounded (e.g. allergy chips)
 * shape='chip'           → square-ish radius (e.g. conditions / meds)
 *
 * variant='default' → neutral grey
 * variant='severe'  → red tones (e.g. severe allergy)
 * variant='muted'   → same grey, used for placeholder text badges
 */
export const PdfBadge = styled.span<{
  variant?: 'default' | 'severe' | 'muted';
  shape?: 'pill' | 'chip';
}>`
  display: inline-block;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: ${({ shape }) => (shape === 'chip' ? '4px' : '12px')};
  font-size: 9.5px;
  font-weight: 500;
  line-height: 1.6;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  background: ${({ variant }) =>
    variant === 'severe' ? '#fde8e8' : '#f2f2f2'};
  color: ${({ variant }) => (variant === 'severe' ? '#c0392b' : '#333')};
  border: 1px solid
    ${({ variant }) => (variant === 'severe' ? '#f5c6c2' : '#e0e0e0')};
`;

export const PdfEmptyText = styled.span`
  font-size: 9.5px;
  color: #aaa;
  font-style: italic;
`;

// ── Two-column layout ─────────────────────────────────────────────────────────

export const PdfTwoColRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  page-break-inside: avoid;
  break-inside: avoid;

  & > * {
    flex: 1;
    min-width: 0;
    margin-bottom: 0;
  }

  @media print {
    page-break-inside: auto;
    break-inside: auto;
  }
`;

// ── Generic data table ────────────────────────────────────────────────────────

export const PdfTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 10.5px;

  th {
    text-align: left;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.6px;
    color: #aaa;
    text-transform: uppercase;
    padding-bottom: 5px;
    border-bottom: 1px solid #e2e2e2;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  td {
    padding: 4px 6px 4px 0;
    vertical-align: top;
    border-bottom: 1px solid #f5f5f5;
    color: #111;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  td:first-child {
    color: #999;
    font-size: 9.5px;
    width: 72px;
  }

  td:nth-child(2) {
    font-weight: 600;
    padding-right: 10px;
  }

  tr:last-child td {
    border-bottom: none;
    padding-bottom: 0;
  }
`;
